import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  Button,
  Pagination,
  TextInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem
} from "@patternfly/react-core";
import { Table, Tbody, Td, Th, Thead, Tr } from "@patternfly/react-table";

export type StandardColumn<T> = {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  value?: (row: T) => string | number | null | undefined;
  sortable?: boolean;
  width?: number | string;
  textCenter?: boolean;
};

type SortState = {
  key: string;
  direction: "asc" | "desc";
};

function cellValue<T>(row: T, column: StandardColumn<T>): string {
  const raw = column.value ? column.value(row) : (row as Record<string, unknown>)[column.key];
  return String(raw ?? "");
}

function searchableText<T>(row: T, columns: StandardColumn<T>[]): string {
  return columns.map(column => cellValue(row, column)).join(" ").toLowerCase();
}

function compareValues(left: string, right: string): number {
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
}

export function StandardDataTable<T>({
  ariaLabel,
  rows,
  columns,
  emptyText = "No records match this view.",
  searchPlaceholder = "Search this table",
  initialPerPage = 25,
  compact = true
}: {
  ariaLabel: string;
  rows: T[];
  columns: StandardColumn<T>[];
  emptyText?: string;
  searchPlaceholder?: string;
  initialPerPage?: number;
  compact?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [sortState, setSortState] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(initialPerPage);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const visible = term ? rows.filter(row => searchableText(row, columns).includes(term)) : rows;
    if (!sortState) return visible;
    const column = columns.find(item => item.key === sortState.key);
    if (!column) return visible;
    return [...visible].sort((left, right) => {
      const result = compareValues(cellValue(left, column), cellValue(right, column));
      return sortState.direction === "asc" ? result : -result;
    });
  }, [columns, rows, search, sortState]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, pageCount);
  const pagedRows = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  const toggleSort = (column: StandardColumn<T>) => {
    if (column.sortable === false) return;
    setPage(1);
    setSortState(current => {
      if (current?.key !== column.key) return { key: column.key, direction: "asc" };
      return { key: column.key, direction: current.direction === "asc" ? "desc" : "asc" };
    });
  };

  return (
    <div className="pf-toj-standard-table">
      <Toolbar className="pf-toj-table-toolbar">
        <ToolbarContent>
          <ToolbarItem className="pf-toj-table-search">
            <TextInput
              aria-label={`${ariaLabel} search`}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(_event, value) => {
                setSearch(String(value));
                setPage(1);
              }}
            />
          </ToolbarItem>
          <ToolbarItem>
            <span className="pf-toj-table-count">{filtered.length} of {rows.length}</span>
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>
      <div className="pf-toj-table-wrap pf-toj-standard-table-wrap">
        <Table aria-label={ariaLabel} variant={compact ? "compact" : undefined}>
          <Thead>
            <Tr>
              {columns.map(column => (
                <Th key={column.key} textCenter={column.textCenter} style={column.width ? { width: column.width } : undefined}>
                  <Button
                    variant="plain"
                    className="pf-toj-sort-button"
                    onClick={() => toggleSort(column)}
                    isDisabled={column.sortable === false}
                  >
                    {column.label}
                    {sortState?.key === column.key ? (sortState.direction === "asc" ? " ▲" : " ▼") : ""}
                  </Button>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {pagedRows.map((row, index) => (
              <Tr key={index}>
                {columns.map(column => (
                  <Td key={column.key} dataLabel={column.label} textCenter={column.textCenter}>
                    {column.render ? column.render(row) : cellValue(row, column)}
                  </Td>
                ))}
              </Tr>
            ))}
            {!pagedRows.length && (
              <Tr>
                <Td colSpan={columns.length}>
                  <div className="pf-toj-empty">
                    <strong>{emptyText}</strong>
                  </div>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </div>
      <Pagination
        itemCount={filtered.length}
        page={safePage}
        perPage={perPage}
        onSetPage={(_event, nextPage) => setPage(nextPage)}
        onPerPageSelect={(_event, nextPerPage) => {
          setPerPage(nextPerPage);
          setPage(1);
        }}
        variant="bottom"
        perPageOptions={[
          { title: "10", value: 10 },
          { title: "25", value: 25 },
          { title: "50", value: 50 },
          { title: "100", value: 100 }
        ]}
      />
    </div>
  );
}
